import type { CSSProperties } from "react";

export type ExposureStatus =
  | "DISCOVERED"
  | "QUEUED"
  | "SUBMITTED"
  | "ACKNOWLEDGED"
  | "REMOVED"
  | "DENIED"
  | "NEEDS_USER_ACTION"
  | "REAPPEARED";

export type ExposureType = "upp" | "sok";

export type Exposure = {
  id: number | string;
  source: string;
  type: ExposureType;
  snippet: string;
  url: string;
  severity: "Hög" | "Medel" | "Låg";
  status: ExposureStatus;
  apiId?: string;
};

const NAVY = "#0b1f4a";
const LINK = "#1a3a7a";

const STATUS_META: Record<ExposureStatus, { label: string; color: string }> = {
  DISCOVERED: { label: "Upptäckt", color: "#9b9797" },
  QUEUED: { label: "I kö", color: LINK },
  SUBMITTED: { label: "Inskickad", color: LINK },
  ACKNOWLEDGED: { label: "Mottagen", color: LINK },
  REMOVED: { label: "Borttagen", color: "#0F7B4B" },
  DENIED: { label: "Nekad", color: "#B42318" },
  NEEDS_USER_ACTION: { label: "Kräver din åtgärd", color: "#C47F00" },
  REAPPEARED: { label: "Återkommen", color: "#B42318" },
};

const SEV_COLOR: Record<Exposure["severity"], string> = {
  Hög: "#C47F00",
  Medel: LINK,
  Låg: "#9b9797",
};

export function statusLabel(status: ExposureStatus): string {
  return STATUS_META[status].label;
}

export function statusColor(status: ExposureStatus): string {
  return STATUS_META[status].color;
}

export function StatusPill({ status }: { status: ExposureStatus }) {
  const { label, color } = STATUS_META[status];
  return (
    <span
      style={{
        fontSize: 11,
        letterSpacing: "0.05em",
        padding: "3px 10px",
        borderRadius: 99,
        border: `1px solid ${color}`,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

export type ExposureCardProps = {
  exposure: Exposure;
  onApprove?: (id: number | string) => void;
  onNotMe?: (id: number | string) => void;
  onOpen?: (id: number | string) => void;
};

export default function ExposureCard({
  exposure,
  onApprove,
  onNotMe,
  onOpen,
}: ExposureCardProps) {
  const canApprove = exposure.status === "DISCOVERED";
  const card: CSSProperties = {
    background: "#fff",
    border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
    borderRadius: 7,
    padding: "20px 24px",
    display: "flex",
    gap: 18,
    alignItems: "center",
  };

  return (
    <div style={card} data-testid={`exposure-${exposure.id}`}>
      <span
        style={{
          width: 40,
          height: 40,
          border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
          borderRadius: 4,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 17,
          color: NAVY,
          flexShrink: 0,
        }}
      >
        {exposure.source[0]}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 15, color: NAVY }}>{exposure.source}</span>
          <span
            style={{
              fontSize: 11,
              padding: "2px 9px",
              borderRadius: 4,
              border: `1px solid ${SEV_COLOR[exposure.severity]}`,
              color: SEV_COLOR[exposure.severity],
            }}
          >
            {exposure.severity}
          </span>
          <StatusPill status={exposure.status} />
        </div>
        <div style={{ fontSize: 13.5, color: "#7d7979", marginTop: 4 }}>{exposure.snippet}</div>
        <div
          style={{
            fontSize: 12,
            color: "#bab6b6",
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {exposure.url}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {canApprove && (
          <button
            type="button"
            onClick={() => onApprove?.(exposure.id)}
            style={{
              padding: "9px 16px",
              border: `1px solid ${NAVY}`,
              borderRadius: 4,
              background: "transparent",
              fontSize: 13,
              color: NAVY,
              cursor: "pointer",
            }}
          >
            Godkänn borttagning
          </button>
        )}
        <button
          type="button"
          onClick={() => onNotMe?.(exposure.id)}
          style={{
            padding: "9px 14px",
            border: "1px solid #d7d3d3",
            borderRadius: 4,
            background: "transparent",
            fontSize: 13,
            color: "#7d7979",
            cursor: "pointer",
          }}
        >
          Inte jag
        </button>
        <button
          type="button"
          onClick={() => onOpen?.(exposure.id)}
          style={{
            padding: "9px 14px",
            border: 0,
            background: "transparent",
            fontSize: 13,
            color: LINK,
            cursor: "pointer",
          }}
        >
          Öppna ↗
        </button>
      </div>
    </div>
  );
}
